import {
  type BackstageIdentityApi,
  type BackstageIdentityResponse,
  type ProfileInfo,
  type ProfileInfoApi,
  SessionState,
  type SessionApi,
} from '@backstage/core-plugin-api';
import {
  type DiscoveryApi,
  createApiRef,
} from '@backstage/frontend-plugin-api';
import type { Observable, Observer } from '@backstage/types';

export type LocalAuthApi = BackstageIdentityApi &
  ProfileInfoApi & {
    localSignIn(
      username: string,
      password: string,
    ): Promise<BackstageIdentityResponse>;
  } & SessionApi;

export const localAuthApiRef = createApiRef<LocalAuthApi>().with({
  id: 'auth.local',
});

export class LocalAuthClient implements LocalAuthApi {
  private identity?: BackstageIdentityResponse;
  private profile?: ProfileInfo;

  constructor(private readonly discoveryApi: DiscoveryApi) {}

  async localSignIn(username: string, password: string) {
    const authBaseUrl = await this.discoveryApi.getBaseUrl('auth');
    const response = await fetch(`${authBaseUrl}/local/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${window.btoa(`${username}:${password}`)}`,
      },
    });
    const body = (await response.json()) as {
      error?: { message?: string };
      profile?: ProfileInfo;
      backstageIdentity?: BackstageIdentityResponse;
    };

    if (!response.ok || !body.backstageIdentity) {
      throw new Error(body.error?.message ?? 'Invalid username or password');
    }

    this.identity = body.backstageIdentity;
    this.profile = body.profile;
    return this.identity;
  }

  async getBackstageIdentity() {
    return this.identity;
  }

  async getProfile() {
    if (!this.profile) {
      throw new Error('Local user is not signed in');
    }
    return this.profile;
  }

  async signIn() {
    // Credentials are submitted through the R-DAP sign-in form.
  }

  async signOut() {
    this.identity = undefined;
    this.profile = undefined;
    window.location.reload();
  }

  sessionState$(): Observable<SessionState> {
    const currentState = this.identity
      ? SessionState.SignedIn
      : SessionState.SignedOut;

    return {
      [Symbol.observable]() {
        return this;
      },
      subscribe(
        observerOrNext?:
          | Observer<SessionState>
          | ((state: SessionState) => void),
      ) {
        const observer =
          typeof observerOrNext === 'function'
            ? { next: observerOrNext }
            : observerOrNext;
        observer?.next?.(currentState);

        return {
          closed: false,
          unsubscribe() {},
        };
      },
    } as Observable<SessionState>;
  }
}
